import { Component, Injector, OnInit } from '@angular/core';
import { ApiService } from '@api/api.service';
import { User } from '@api/types';
import { RavIcon } from '@shared/icon/icon';
import { ModalService } from '@shared/modal/modal.service';
import { ContentfulService } from '@shared/services/contentful.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { ActionTrigger, RowsActions, TableActions } from '@shared/table/actions/action';
import { TableConfig } from '@shared/types/table';
import { get } from 'lodash';
import { UserRoleGroup } from 'src/app/security/role.service';
import { AccountE2eEncryption } from './../../../api/types/account';
import { UsersGridDataSource } from './users.datasource';

@Component({
  selector: 'rav-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit {
  UserRoleGroup = UserRoleGroup;
  dataSource: UsersGridDataSource;
  tableConfig: TableConfig;
  message: string = null;
  messageType = '';
  ravenTelephone: number;
  accountID: string = null;

  get calloutClasses(): string {
    return `small ${this.messageType} callout margin-top-1`;
  }

  constructor(
    injector: Injector,
    private modal: ModalService,
    private api: ApiService,
    private contentful: ContentfulService,
    public localStorage: LocalStorageService
  ) {
    this.dataSource = new UsersGridDataSource(injector);
    this.tableConfig = {
      columns: [
        { key: 'first_name' },
        { key: 'last_name' },
        { key: 'email' },
        { key: 'role' },
        { key: 'status' },
        { key: 'enable_2fa', header: '2FA' },
        { key: 'last_activity' }
      ],
      mobileSettings: {
        titleKeys: ['first_name', 'last_name'],
        subtitleKey: 'role',
        subtitleHeader: 'Role',
        icon: RavIcon.User
      },
      actions: [TableActions.createNewUser, TableActions.editUser, TableActions.deleteUser],
      isSearchable: false
    };
    if (!localStorage.userWithToken || !localStorage.userWithToken.account) {
      return;
    }

    // if E2eEncryption is NOT enabled, we add the Reset Password column to the table
    if (
      localStorage.userWithToken &&
      localStorage.userWithToken.account &&
      localStorage.userWithToken.account.e2e_encryption === AccountE2eEncryption.Inactive
    ) {
      this.tableConfig.columns.push({ key: 'Reset Password', action: RowsActions.resetPassword });
    }

    this.accountID = get(localStorage, 'userWithToken.account.id', '');
  }

  ngOnInit() {
    this.modal.close.subscribe((email: string) => {
      this.dataSource.reloadData.emit();
    });
    this.contentful.getContactInfo().then((tel) => {
      this.ravenTelephone = tel[0].fields.phoneDisplay;
    });
  }

  /**
   * parse and execute the appropriate action
   *
   * @param action performed by the user.
   */
  onActionsTriggered(action: ActionTrigger<User>): void {
    this.message = null;
    switch (action.name) {
      case TableActions.createNewUser.name:
        this.modal.open.next({ modalId: 'newUser' });
        break;
      case TableActions.deleteUser.name:
        this.deleteUsers(action.data.map((user) => user.id));
        break;
      case TableActions.editUser.name:
        this.modal.open.next({ modalId: 'newUser', selectedUser: action.data[0] });
        break;
      case RowsActions.resetPassword.name:
        this.sendResetPassword(action.data[0].email);
        break;
    }
  }

  /**
   * Makes a request to send the reset password email.
   *
   * @param email to which the email will be sent.
   */
  sendResetPassword(email: string): void {
    this.api.users.password.email.post({ email }).subscribe(() => {
      this.message = `Reset password email has been sent successfully to ${email}`;
      this.messageType = 'success';
      this.dataSource.reloadData.emit();
    });
  }

  /**
   * Deletes the given users
   *
   * @param ids of the users to be deleted
   */
  async deleteUsers(ids: string[]): Promise<void> {
    const objectOfDeletion = ids.length > 1 ? `these ${ids.length} users` : 'this user';
    if (!confirm(`Are you sure you want to delete ${objectOfDeletion}?`)) return;

    // TODO: Add try/catch for error handling
    const deleteRequests: Promise<object>[] = [];
    ids.forEach((id) => {
      if (id === this.localStorage.userWithToken.id) {
        this.message = `You are unable to delete your own user.
          Please call ${this.ravenTelephone} if you wish to deactivate your account.`;
        this.messageType = 'warning';
        return;
      }
      deleteRequests.push(this.api.users.byId.delete(id).toPromise());
    });
    await Promise.all(deleteRequests);

    this.dataSource.reloadData.emit();
  }
}
